import { AHText, Box, TouchableBox } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";

import { ScrollView, View } from "react-native";
import React, { useDeferredValue, useState } from "react";
import { useNavigation } from "@react-navigation/native";

import { useWorkspaceId } from "../workspaceId";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { TimeSeriesOverviewItem } from "./TimeSeriesOverviewItem";
import { useOrgListStyleProps } from "../useOrgHomeDetailContainerStyle";

import { WorkspaceAnalyticsMainQuery } from "./__generated__/WorkspaceAnalyticsMainQuery.graphql";
import { SinglePeriodTableTopAndBottom } from "./SinglePeriodTableTopAndBottom";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import {
  AnalyticsTimeResolution,
  WorkspaceAnalyticsDimensionType,
} from "./__generated__/WorkspaceAnalyticsDetailQuery.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useMetricToLabel } from "./metricToLabel";

export function WorkspaceAnalyticsMainScreen() {
  const wId = useWorkspaceId();

  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <WorkspaceAnalyticsScreenComponent workspaceId={wId} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WorkspaceAnalyticsScreenComponent(props: {
  workspaceId: string;
  fetchKey: string;
}) {
  const navigation = useNavigation<any>();

  const [groupByDimension, setGroupByDimension] =
    useState<WorkspaceAnalyticsDimensionType>("STORE");

  const [timeResolution, setTimeResolution] =
    useState<AnalyticsTimeResolution>("WEEK");

  const groupByDimensionDeffered = useDeferredValue(groupByDimension);
  const timeResolutionDeffered = useDeferredValue(timeResolution);

  const data = useLazyLoadQuery<WorkspaceAnalyticsMainQuery>(
    graphql`
      query WorkspaceAnalyticsMainQuery(
        $workspaceId: ID!
        $groupByDimension: WorkspaceAnalyticsDimensionType
        $timeResolution: AnalyticsTimeResolution
      ) {
        workspace(id: $workspaceId) @required(action: THROW) {
          taskExecutionsCompleted: analytics(
            metric: TASK_EXECUTIONS_COMPLETED
          ) {
            connection(
              first: 2
              skip: 1
              groupByDimension: $groupByDimension
              timeResolution: $timeResolution
            ) {
              edges {
                node {
                  formattedDate
                }
              }
            }
            ...TimeSeriesOverviewItem_data
          }
          todoIssueCreated: analytics(metric: TODO_ISSUE_CREATED) {
            ...TimeSeriesOverviewItem_data
          }
          todoIssueResolved: analytics(metric: TODO_ISSUE_RESOLVED) {
            ...TimeSeriesOverviewItem_data
          }
          todoIssueResolvedAvgTime: analytics(
            metric: TODO_ISSUE_RESOLVED_AVG_TIME
          ) {
            ...TimeSeriesOverviewItem_data
          }

          todoCreated: analytics(metric: TODO_CREATED) {
            ...TimeSeriesOverviewItem_data
          }
          todoResolved: analytics(metric: TODO_RESOLVED) {
            ...TimeSeriesOverviewItem_data
          }
          todoTTR: analytics(metric: TODO_RESOLVED_AVG_TIME) {
            ...TimeSeriesOverviewItem_data
          }
          productDateTrackerRegistered: analytics(
            metric: PRODUCT_DATE_TRACKER_REGISTERED
          ) {
            ...TimeSeriesOverviewItem_data
          }
          productDateTrackerCompleted: analytics(
            metric: PRODUCT_DATE_TRACKER_TASKS_COMPLETED
          ) {
            ...TimeSeriesOverviewItem_data
          }

          singlePeriodAnalytics(
            groupByDimension: $groupByDimension
            timeResolution: $timeResolution
          ) {
            ...SinglePeriodTableTopAndBottom
          }
        }
      }
    `,
    {
      workspaceId: props.workspaceId,
      groupByDimension: groupByDimensionDeffered,
      timeResolution: timeResolutionDeffered,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const detailLayout = useOrgListStyleProps();
  const intl_tokens = useIntlTokens();
  const metricToLabel = useMetricToLabel();
  return (
    <View style={{ flex: 1 }}>
      <ScrollView {...detailLayout}>
        <Box style={{ flexDirection: "row" }} gap="medium" spaceTop="medium">
          <SegmentedControlNew
            items={[
              {
                children: <AHText type="subtitle">M</AHText>,
                width: 40,
                value: "MONTH" satisfies AnalyticsTimeResolution,
              },
              {
                width: 40,
                children: <AHText type="subtitle">W</AHText>,
                value: "WEEK" satisfies AnalyticsTimeResolution,
              },
            ]}
            selected={timeResolution}
            setSelected={(selected) => {
              setTimeResolution(selected);
            }}
          />
          <SegmentedControlNew
            items={[
              {
                children: <AHText type="subtitle">{intl_tokens.Unit}</AHText>,
                width: 100,
                value: "STORE" satisfies WorkspaceAnalyticsDimensionType,
              },
              {
                width: 100,
                children: <AHText type="subtitle">Workspace</AHText>,
                value:
                  "WORKSPACE_OWNER" satisfies WorkspaceAnalyticsDimensionType,
              },
            ]}
            selected={groupByDimension}
            setSelected={(selected) => {
              setGroupByDimension(selected);
            }}
          />
        </Box>
        <Box spaceTop="medium">
          <AHText type="title-big">
            {
              data.workspace.taskExecutionsCompleted?.connection.edges[0].node
                .formattedDate
            }
          </AHText>
        </Box>
        <Box
          spaceTop="small"
          style={{
            flexDirection: "column",
            minWidth: "100%",
          }}
        >
          <Section title={intl_tokens.Checklists}>
            <TimeSeriesOverviewItem
              data={data.workspace.taskExecutionsCompleted}
              title={metricToLabel("TASK_EXECUTIONS_COMPLETED")[0]}
              subTitle={metricToLabel("TASK_EXECUTIONS_COMPLETED")[1]}
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TASK_EXECUTIONS_COMPLETED",
                });
              }}
            />
            <TimeSeriesOverviewItem
              data={data.workspace.todoIssueCreated}
              title={metricToLabel("TODO_ISSUE_CREATED")[0]}
              subTitle={metricToLabel("TODO_ISSUE_CREATED")[1]}
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TODO_ISSUE_CREATED",
                });
              }}
            />
            <TimeSeriesOverviewItem
              data={data.workspace.todoIssueResolved}
              title={metricToLabel("TODO_ISSUE_RESOLVED")[0]}
              subTitle={metricToLabel("TODO_ISSUE_RESOLVED")[1]}
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TODO_ISSUE_RESOLVED",
                });
              }}
            />
            <TimeSeriesOverviewItem
              data={data.workspace.todoIssueResolvedAvgTime}
              title={metricToLabel("TODO_ISSUE_RESOLVED_AVG_TIME")[0]}
              subTitle={metricToLabel("TODO_ISSUE_RESOLVED_AVG_TIME")[1]}
              mode="avg"
              trendMode="small-is-better"
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TODO_ISSUE_RESOLVED_AVG_TIME",
                });
              }}
            />
          </Section>
          <Section title={intl_tokens.Todos}>
            <TimeSeriesOverviewItem
              data={data.workspace.todoCreated}
              title={metricToLabel("TODO_CREATED")[0]}
              subTitle={metricToLabel("TODO_CREATED")[1]}
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TODO_CREATED",
                });
              }}
            />
            <TimeSeriesOverviewItem
              data={data.workspace.todoResolved}
              title={metricToLabel("TODO_RESOLVED")[0]}
              subTitle={metricToLabel("TODO_RESOLVED")[1]}
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TODO_RESOLVED",
                });
              }}
            />

            <TimeSeriesOverviewItem
              data={data.workspace.todoTTR}
              mode="avg"
              trendMode="small-is-better"
              title={metricToLabel("TODO_RESOLVED_AVG_TIME")[0]}
              subTitle={metricToLabel("TODO_RESOLVED_AVG_TIME")[1]}
              onPress={() => {
                navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                  metric: "TODO_RESOLVED_AVG_TIME",
                });
              }}
            />
          </Section>

          {data.workspace.productDateTrackerCompleted ||
          data.workspace.productDateTrackerRegistered ? (
            <Section title={intl_tokens.Products}>
              <TimeSeriesOverviewItem
                data={data.workspace.productDateTrackerRegistered}
                title={metricToLabel("PRODUCT_DATE_TRACKER_REGISTERED")[0]}
                subTitle={metricToLabel("PRODUCT_DATE_TRACKER_REGISTERED")[1]}
                onPress={() => {
                  navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                    metric: "PRODUCT_DATE_TRACKER_REGISTERED",
                  });
                }}
              />
              <TimeSeriesOverviewItem
                data={data.workspace.productDateTrackerCompleted}
                title={metricToLabel("PRODUCT_DATE_TRACKER_TASKS_COMPLETED")[0]}
                subTitle={
                  metricToLabel("PRODUCT_DATE_TRACKER_TASKS_COMPLETED")[1]
                }
                onPress={() => {
                  navigation.navigateDeprecated("WorkspaceAnalyticsDetail", {
                    metric: "PRODUCT_DATE_TRACKER_TASKS_COMPLETED",
                  });
                }}
              />
            </Section>
          ) : null}
        </Box>
        <TouchableBox
          onPress={() => {
            navigation.navigateDeprecated(
              "WorkspaceAnalyticsSinglePeriodDetail",
              {}
            );
          }}
          spaceTop="large"
          style={{ flexDirection: "row", minWidth: "100%", flexWrap: "wrap" }}
        >
          {data.workspace.singlePeriodAnalytics ? (
            <SinglePeriodTableTopAndBottom
              data={data.workspace.singlePeriodAnalytics}
              title={"Leaderboard"}
            />
          ) : null}
        </TouchableBox>
      </ScrollView>
    </View>
  );
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <Box spaceVertical="small">
      <AHText type="info-small" color="washedText">
        {props.title}
      </AHText>
      <Box
        spaceTop="small"
        gap="medium"
        style={{ flexDirection: "row", flexWrap: "wrap" }}
      >
        {props.children}
      </Box>
    </Box>
  );
}
